import Link from 'next/link';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { Button, Checkbox, FieldHint, TextField } from 'ui';
import { emailPattern, isEmptyObject, passwordPattern } from 'utils';

import { useTranslation } from '@/i18n/client';
import { signUp, SignUpPayload } from '@/modules/account/api';
import { routes } from '@/shared/data/routes';
import { useCurrentLocale } from '@/shared/utils/client';

import styles from './SignUpForm.module.scss';
import { SignUpFormModel, SignUpFormProps } from './types';

export const SignUpForm = ({ setFormSent }: SignUpFormProps) => {
  const { t } = useTranslation(['header', 'common', 'checkout']);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<SignUpFormModel>();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const locale = useCurrentLocale();

  const onSubmit = async (data: SignUpFormModel) => {
    setIsLoading(true);

    const formData: SignUpPayload = {
      email: data.email,
      plainPassword: data.password,
    };

    const result = await signUp(formData);

    if (!result.customerId) {
      setError('email', { message: t('authModal.existingUser') || '' });
      setIsLoading(false);
      return;
    }

    setFormSent(formData.email);
  };

  return (
    <>
      <div className={styles.signUpForm__formWrapper}>
        <h2 className={styles.signUpForm__title}>
          {t('authModal.signUpTitle')}
        </h2>
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            label={t('common:common.emailAddress')}
            {...register('email', {
              required: t('checkout:details.fieldRequired') || '',
              pattern: {
                value: emailPattern,
                message: t('checkout:details.invalidEmail') || '',
              },
            })}
            hasError={!!errors.email}
          />
          {errors.email ? (
            <FieldHint isError>{errors.email?.message}</FieldHint>
          ) : null}
          <TextField
            type={showPassword ? 'text' : 'password'}
            className={styles.signUpForm__passwordField}
            label={t('common:common.password')}
            suffix={
              <Button
                buttonName="showButton"
                type="button"
                variant="iconButton"
                onPress={() => setShowPassword((oldState) => !oldState)}
              >
                {showPassword
                  ? t('common:common.hide')
                  : t('common:common.show')}
              </Button>
            }
            {...register('password', {
              required: t('checkout:details.fieldRequired') || '',
              pattern: {
                value: passwordPattern,
                message: t('authModal.invalidPassword') || '',
              },
            })}
            hasError={!!errors.password}
          />
          {errors.password ? (
            <FieldHint isError>{errors.password?.message}</FieldHint>
          ) : null}
          <p className={styles.signUpForm__privacyPolicy}>
            <Checkbox
              {...register('privacyPolicy', {
                required: t('checkout:details.fieldRequired') || '',
              })}
              hasError={!!errors.privacyPolicy}
            />
            <div>
              <Trans
                i18nKey="authModal.privacyPolicy"
                t={t}
                components={[
                  <Link href={routes[locale].RULES} key={0} />,
                  <Link href={routes[locale].PRIVACY_POLICY} key={1} />,
                ]}
              />
            </div>
          </p>
          <Button
            fullWidth
            variant="blue"
            type="submit"
            isLoading={isLoading}
            isDisabled={isLoading || !isEmptyObject(errors)}
          >
            {t('authModal.signUp')}
          </Button>
        </form>
      </div>
      {/* <SocialSign /> */}
    </>
  );
};
